import { useState, useEffect, useMemo, useRef } from 'react';
import { Zap, X, MapPin, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { differenceInMinutes, differenceInHours, format } from 'date-fns';
import type { Event } from '../types/event';

interface NowStripProps {
  events: Event[];
  onEventClick: (event: Event) => void;
}

interface CategorizedEvent {
  event: Event;
  isNow: boolean;
  timeLabel: string;
}

function formatTimeLabel(event: Event, now: Date): { isNow: boolean; timeLabel: string } | null {
  if (!event.start_datetime) return null;
  const start = new Date(event.start_datetime);
  const end = event.end_datetime ? new Date(event.end_datetime) : null;
  const minsUntilStart = differenceInMinutes(start, now);
  const minsFromStart = differenceInMinutes(now, start);

  // Happening now: started ≤ 4h ago and either end is in the future or no end time
  if (minsFromStart >= 0 && (end ? end > now : minsFromStart <= 240)) {
    let timeLabel = 'Ongoing';
    if (end) {
      const minsLeft = differenceInMinutes(end, now);
      if (minsLeft < 60) timeLabel = `ends in ${minsLeft}m`;
      else timeLabel = `ends in ${differenceInHours(end, now)}h`;
    } else {
      timeLabel = `started ${minsFromStart < 60 ? `${minsFromStart}m` : `${differenceInHours(now, start)}h`} ago`;
    }
    return { isNow: true, timeLabel };
  }

  // Starting soon: within the next 3 hours
  if (minsUntilStart > 0 && minsUntilStart <= 180) {
    let timeLabel: string;
    if (minsUntilStart < 60) timeLabel = `in ${minsUntilStart}m`;
    else {
      const h = differenceInHours(start, now);
      const m = minsUntilStart - h * 60;
      timeLabel = m > 0 ? `in ${h}h ${m}m` : `in ${h}h`;
    }
    return { isNow: false, timeLabel };
  }

  return null;
}

export default function NowStrip({ events, onEventClick }: NowStripProps) {
  const [now, setNow] = useState(() => new Date());
  const [dismissed, setDismissed] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  const scrollByDir = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -240 : 240, behavior: 'smooth' });
  };

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(id);
  }, []);

  const items = useMemo((): CategorizedEvent[] => {
    const result: CategorizedEvent[] = [];
    for (const event of events) {
      const parsed = formatTimeLabel(event, now);
      if (!parsed) continue;
      result.push({ event, ...parsed });
    }
    // Sort: happening now first, then by start time
    return result.sort((a, b) => {
      if (a.isNow !== b.isNow) return a.isNow ? -1 : 1;
      const aTime = a.event.start_datetime ? new Date(a.event.start_datetime).getTime() : 0;
      const bTime = b.event.start_datetime ? new Date(b.event.start_datetime).getTime() : 0;
      return aTime - bTime;
    });
  }, [events, now]);

  useEffect(() => {
    updateScrollButtons();
  }, [items.length]);

  if (dismissed || items.length === 0) return null;

  const nowCount = items.filter(i => i.isNow).length;
  const soonCount = items.filter(i => !i.isNow).length;

  return (
    <div className="bg-slate-800 border-b border-slate-700/60 flex-shrink-0 flex items-center min-h-0">
      {/* Left anchor */}
      <div className="flex items-center gap-2 px-3 py-2 border-r border-slate-700/60 flex-shrink-0 self-stretch">
        <div className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </div>
        <div className="flex flex-col leading-none gap-0.5">
          <span className="text-[10px] font-bold text-white uppercase tracking-widest flex items-center gap-1">
            <Zap className="w-2.5 h-2.5 text-emerald-400" />
            Live
          </span>
          <span className="text-[10px] text-slate-400 whitespace-nowrap">
            {nowCount > 0 && `${nowCount} now`}
            {nowCount > 0 && soonCount > 0 && ' · '}
            {soonCount > 0 && `${soonCount} soon`}
          </span>
        </div>
      </div>

      {/* Left scroll button (desktop) */}
      {canScrollLeft && (
        <button
          onClick={() => scrollByDir('left')}
          className="hidden md:flex flex-shrink-0 self-stretch items-center justify-center px-2 text-slate-500 hover:text-slate-200 hover:bg-slate-700/40 transition-colors"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}

      {/* Scrollable cards */}
      <div
        ref={scrollRef}
        onScroll={updateScrollButtons}
        className="flex items-center gap-2 overflow-x-auto px-3 py-2 flex-1 min-w-0 scrollbar-hide md:[scrollbar-width:auto] md:[-ms-overflow-style:auto]"
      >
        {items.map(({ event, isNow, timeLabel }) => (
          <button
            key={event.id}
            onClick={() => onEventClick(event)}
            className={`flex-shrink-0 flex items-center gap-2.5 pl-3 pr-3.5 py-1.5 rounded-lg border transition-all duration-150 cursor-pointer text-left group ${
              isNow
                ? 'bg-emerald-500/10 border-emerald-500/25 hover:bg-emerald-500/20 hover:border-emerald-500/40'
                : 'bg-amber-500/10 border-amber-500/20 hover:bg-amber-500/20 hover:border-amber-500/35'
            }`}
          >
            {/* Indicator */}
            {isNow ? (
              <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400" />
              </span>
            ) : (
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400 flex-shrink-0" />
            )}

            {/* Content */}
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-100 truncate max-w-[160px] leading-snug group-hover:text-white transition-colors">
                {event.title}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className={`text-[11px] font-medium ${isNow ? 'text-emerald-400' : 'text-amber-400'}`}>
                  {isNow && (
                    <span className="inline-flex items-center gap-0.5">
                      <Clock className="w-2.5 h-2.5" />
                      {' '}
                    </span>
                  )}
                  {timeLabel}
                </span>
                {event.venue_name && (
                  <>
                    <span className="text-slate-600 text-[11px]">·</span>
                    <span className="text-[11px] text-slate-400 truncate max-w-[100px] flex items-center gap-0.5">
                      <MapPin className="w-2 h-2 flex-shrink-0" />
                      {event.venue_name}
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Time badge */}
            {event.start_datetime && (
              <span className="text-[10px] font-medium text-slate-500 flex-shrink-0 tabular-nums ml-1">
                {format(new Date(event.start_datetime), 'h:mm a')}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Right scroll button (desktop) */}
      {canScrollRight && (
        <button
          onClick={() => scrollByDir('right')}
          className="hidden md:flex flex-shrink-0 self-stretch items-center justify-center px-2 text-slate-500 hover:text-slate-200 hover:bg-slate-700/40 transition-colors"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}

      {/* Dismiss */}
      <button
        onClick={() => setDismissed(true)}
        className="flex-shrink-0 self-stretch flex items-center justify-center px-3 text-slate-500 hover:text-slate-300 border-l border-slate-700/60 transition-colors"
        title="Dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
